import type { ChatErrorCode, ChatEvent, ChatOutcome, ChatSource } from '~~/shared/types/assistant';

/**
 * Website chat client (contracts/public-api.md, research R3): POSTs the question with the CSRF
 * header and reads the Server-Sent Events stream with a ReadableStream reader (EventSource
 * cannot POST or send headers). The conversation id lives in sessionStorage.
 */

export interface AssistantChatMessage {
  /** Local id for rendering; the server message id arrives with the `meta` event. */
  localId: string;
  role: 'user' | 'assistant';
  text: string;
  messageId?: string;
  sources: ChatSource[];
  outcome?: ChatOutcome;
  offerHuman: boolean;
  error?: { code: ChatErrorCode; message: string; retryAfterSec?: number };
  streaming: boolean;
}

export interface AssistantStatus {
  enabled: boolean;
  disclosureVersion: number;
  limits: { messageMaxChars: number };
}

const CONVERSATION_KEY = 'ws-assistant-conversation';
const TOKEN_KEY = 'ws-assistant-conversation-token';

export function useAssistantChat() {
  const auth = useAuth();
  const messages = ref<AssistantChatMessage[]>([]);
  const sending = ref(false);
  const conversationId = ref<string | null>(readStorage(CONVERSATION_KEY));
  /** Proves this browser owns the conversation (the server stores only its hash). */
  const conversationToken = ref<string | null>(readStorage(TOKEN_KEY));
  let controller: AbortController | null = null;

  async function send(text: string): Promise<void> {
    const message = text.trim();
    if (!message || sending.value) {
      return;
    }
    sending.value = true;
    messages.value.push(newMessage('user', message));
    const answer = reactive(newMessage('assistant', '')) as AssistantChatMessage;
    answer.streaming = true;
    messages.value.push(answer);
    controller = new AbortController();

    try {
      const response = await fetch('/api/assistant/chat', {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'X-CSRF-Token': (await auth.getCsrfToken()) ?? '' },
        body: JSON.stringify({ message, conversationId: conversationId.value, conversationToken: conversationToken.value, pageUrl: window.location.href }),
        signal: controller.signal,
      });
      if (!response.ok || !response.body) {
        answer.error = await httpError(response);
        return;
      }
      await readEvents(response.body, event => apply(answer, event));
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        answer.error = { code: 'unavailable', message: 'Connection lost. Please try again.' };
      }
    } finally {
      answer.streaming = false;
      sending.value = false;
      controller = null;
    }
  }

  function apply(answer: AssistantChatMessage, event: ChatEvent): void {
    switch (event.event) {
      case 'meta':
        answer.messageId = event.data.messageId;
        if (event.data.conversationId) {
          conversationId.value = event.data.conversationId;
          writeStorage(CONVERSATION_KEY, event.data.conversationId);
        }
        if (event.data.conversationToken) {
          conversationToken.value = event.data.conversationToken;
          writeStorage(TOKEN_KEY, event.data.conversationToken);
        }
        break;
      case 'delta':
        answer.text += event.data.text;
        break;
      case 'sources':
        answer.sources = event.data.items;
        break;
      case 'done':
        answer.outcome = event.data.outcome;
        answer.offerHuman = event.data.offerHuman;
        break;
      case 'error':
        answer.error = event.data;
        break;
    }
  }

  /** Starts a new conversation (the previous one stays on the server until retention deletes it). */
  function reset(): void {
    controller?.abort();
    messages.value = [];
    conversationId.value = null;
    conversationToken.value = null;
    removeStorage(CONVERSATION_KEY);
    removeStorage(TOKEN_KEY);
  }

  onBeforeUnmount(() => controller?.abort());

  return { messages, sending, conversationId, conversationToken, send, reset };
}

/** Parses an SSE byte stream into typed chat events. */
export async function readEvents(body: ReadableStream<Uint8Array>, onEvent: (event: ChatEvent) => void): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    let boundary = buffer.indexOf('\n\n');
    while (boundary >= 0) {
      const chunk = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const parsed = parseChunk(chunk);
      if (parsed) {
        onEvent(parsed);
      }
      boundary = buffer.indexOf('\n\n');
    }
  }
}

function parseChunk(chunk: string): ChatEvent | null {
  let event = '';
  const data: string[] = [];
  for (const line of chunk.split('\n')) {
    if (line.startsWith('event:')) {
      event = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      data.push(line.slice(5).trimStart());
    }
  }
  if (!event || data.length === 0) {
    return null;
  }
  try {
    return { event, data: JSON.parse(data.join('\n')) } as ChatEvent;
  } catch {
    return null;
  }
}

async function httpError(response: Response): Promise<AssistantChatMessage['error']> {
  if (response.status === 429) {
    const body = await response.json().catch(() => null) as { data?: { retryAfterSec?: number } } | null;
    const retryAfterSec = body?.data?.retryAfterSec ?? Number(response.headers.get('retry-after') ?? 0);
    return { code: 'limit', message: 'You have reached the question limit. Please wait a little before asking again.', retryAfterSec };
  }
  if (response.status === 400) {
    return { code: 'unavailable', message: 'Your message could not be sent. Messages can be up to 2000 characters.' };
  }
  return { code: 'unavailable', message: 'The assistant could not answer right now. Please try again in a minute.' };
}

let localCounter = 0;

function newMessage(role: 'user' | 'assistant', text: string): AssistantChatMessage {
  localCounter++;
  return { localId: `m${Date.now()}-${localCounter}`, role, text, sources: [], offerHuman: false, streaming: false };
}

function readStorage(key: string): string | null {
  try {
    return import.meta.client ? window.sessionStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Private mode or blocked storage: the conversation simply restarts on reload.
  }
}

function removeStorage(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}
